import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet-async';

import { ExternalLink, NamedLink } from '../../components';
import { dispatchOpenBrowseMegaMenu } from '../../util/browseMegaMenuEvents';
import heroSlideOneImage from '../../assets/image/promotixbackgroundimage.jpg';
import heroResellTicketsImage from '../../assets/promotix/hero-resell-tickets.jpg';
import css from './SearchPage.module.css';

const HERO_SLIDE_INTERVAL = 7000;

/** Keeps slide index when the hero unmounts (e.g. mobile map open) and remounts on close. */
let heroSlideResumeIndex = 0;

const HERO_SLIDES = [
  {
    title: 'Discover\u00A0events',
    description:
      'Find and purchase tickets to theater, concerts, festivals, sports games, and more.',
    backgroundImage: heroSlideOneImage,
    cta: { type: 'browseMenu', label: 'Browse events' },
  },
  {
    title: 'Create events',
    description: 'Market your event, sell tickets, and scan\nQR codes at the door.',
    // HubSpot hs-fs supports unsigned width/quality params. 2000px was far larger than the
    // 420px-tall hero needs; 1600 covers desktop while roughly halving the bytes.
    backgroundImage:
      'https://www.promotix.com/hs-fs/hubfs/IMG_0890.jpg?width=1600&name=IMG_0890.jpg',
    /** Black overlay opacity on top of background (0.5 ≈ 50% darker). */
    backgroundDarken: 0.5,
    cta: { type: 'external', label: 'Learn More', href: 'https://promotix.com/event-creators' },
  },
  {
    title: 'Resell tickets',
    description: 'PromoTix helps connect ticket buyers\nwith event ticket resellers.',
    backgroundImage: heroResellTicketsImage,
    cta: { type: 'sellTicket', label: 'Sell a ticket' },
  },
];

const HeroSlideCta = ({ cta }) => {
  if (!cta) {
    return null;
  }

  if (cta.type === 'browseMenu') {
    return (
      <button
        type="button"
        className={css.heroCta}
        onClick={() => dispatchOpenBrowseMegaMenu()}
      >
        {cta.label}
      </button>
    );
  }

  if (cta.type === 'sellTicket') {
    return (
      <NamedLink name="NewListingPage" className={css.heroCta}>
        {cta.label}
      </NamedLink>
    );
  }

  if (cta.type === 'external' && cta.href) {
    return (
      <ExternalLink href={cta.href} className={css.heroCta}>
        {cta.label}
      </ExternalLink>
    );
  }

  return null;
};

/**
 * Hero carousel for SearchPageWithMap. State lives here so slide rotation does not
 * re-render the whole search page (which would reset TopbarSearchForm via enableReinitialize).
 */
const SearchPageHeroCarousel = () => {
  const [activeHeroSlide, setActiveHeroSlide] = useState(
    () => heroSlideResumeIndex % HERO_SLIDES.length
  );
  const intervalRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    intervalRef.current = setInterval(() => {
      setActiveHeroSlide(prev => {
        const next = (prev + 1) % HERO_SLIDES.length;
        heroSlideResumeIndex = next;
        return next;
      });
    }, HERO_SLIDE_INTERVAL);
  }, [clearTimer]);

  useEffect(() => {
    startTimer();
    return clearTimer;
  }, [startTimer, clearTimer]);

  const setHeroSlide = index => {
    heroSlideResumeIndex = index;
    startTimer();
    setActiveHeroSlide(index);
  };

  const activeSlide = HERO_SLIDES[activeHeroSlide];
  const heroDimmed =
    activeSlide.backgroundDarken != null && activeSlide.backgroundDarken > 0;

  return (
    <>
      {/* The hero is the page's LCP element but loads as a CSS background, so the browser
          can't discover it until render-blocking CSS parses. Preload the first slide (the
          only one shown on initial load) at high priority so its fetch starts at navigation.
          href must match the background url() exactly for the browser to reuse the preload. */}
      <Helmet>
        <link rel="preload" as="image" href={HERO_SLIDES[0].backgroundImage} fetchpriority="high" />
      </Helmet>
      <div
        className={classNames(css.topBackgroundImage, {
          [css.heroBackgroundDim]: heroDimmed,
        })}
        style={{ backgroundImage: `url(${activeSlide.backgroundImage})` }}
      >
        <div className={css.imageText}>
          <h1>{activeSlide.title}</h1>
          <p>{activeSlide.description}</p>
          <HeroSlideCta cta={activeSlide.cta} />
        </div>
        <div className={css.carouselDots}>
          {HERO_SLIDES.map((slide, index) => {
            const isActive = activeHeroSlide === index;
            return (
              <button
                key={slide.title}
                type="button"
                className={classNames(css.carouselDot, { [css.carouselDotActive]: isActive })}
                aria-label={`Show slide ${index + 1}`}
                onClick={() => setHeroSlide(index)}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

export default SearchPageHeroCarousel;
